import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader, Card } from "../components/UI";
import { GLOSSARY, GLOSSARY_CATEGORIES } from "../lib/glossary";
import { Search, BookOpen } from "lucide-react";

export default function HelpCenter() {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [cat, setCat] = useState("");

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return GLOSSARY.filter((g) => {
      if (cat && g.cat !== cat) return false;
      if (!needle) return true;
      const hay = `${g.term} ${g.def} ${(g.aliases || []).join(" ")} ${g.cat}`.toLowerCase();
      return needle.split(/\s+/).every((w) => hay.includes(w));
    });
  }, [q, cat]);

  const grouped = useMemo(() => {
    const m = {};
    results.forEach((g) => { (m[g.cat] = m[g.cat] || []).push(g); });
    return m;
  }, [results]);

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="help-center-page">
      <PageHeader
        eyebrow="Help"
        title="Help & Glossary"
        subtitle="Every term used across TotalReach — campaigns, buying, channels, targeting, creative, compliance, fundraising, AI and reporting — searchable in plain language."
      />

      <div className="sticky top-0 z-10 -mx-2 px-2 py-3 bg-[#0B1120]/90 backdrop-blur-md">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='Search any term — try "margin", "CPM", "CTV", "disclaimer"…'
            data-testid="glossary-search-input"
            className="w-full bg-white/5 border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-cyan-400/50 placeholder:text-slate-600"
            autoFocus
          />
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          <button onClick={() => setCat("")} data-testid="glossary-cat-all"
            className={`text-[11px] px-3 py-1 rounded-full border transition-colors ${!cat ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-300" : "border-white/10 text-slate-400 hover:text-white"}`}>
            All ({GLOSSARY.length})
          </button>
          {GLOSSARY_CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(cat === c ? "" : c)} data-testid={`glossary-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`text-[11px] px-3 py-1 rounded-full border transition-colors ${cat === c ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-300" : "border-white/10 text-slate-400 hover:text-white"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {results.length === 0 ? (
        <Card className="mt-4">
          <div className="py-10 text-center">
            <BookOpen className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <div className="text-sm text-slate-300">No matching terms</div>
            <p className="text-xs text-slate-500 mt-1">Try a shorter search — e.g. "margin", "reach", "voter file".</p>
          </div>
        </Card>
      ) : (
        Object.entries(grouped).map(([c, terms]) => (
          <div key={c} className="mt-6">
            <div className="text-xs uppercase tracking-wider text-cyan-300 font-semibold mb-3">{c}</div>
            <div className="grid md:grid-cols-2 gap-3">
              {terms.map((g) => (
                <Card key={g.term} className="!p-4" data-testid={`glossary-term-${g.term.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="font-display font-semibold text-sm">{g.term}</div>
                  {g.aliases && <div className="text-[10px] text-slate-600 mt-0.5">Also: {g.aliases.join(", ")}</div>}
                  <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{g.def}</p>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
